import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { MailIcon, MessageSquareIcon, CheckIcon } from 'lucide-react';
export const ContactPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    // Required fields
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    // Simulate form submission
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-4050">
      <Navbar scrolled={scrolled} data-id="element-4051" />
      <main className="pt-24 pb-20" data-id="element-4052">
        <div className="container mx-auto px-4 md:px-6" data-id="element-4053">
          <div className="max-w-6xl mx-auto" data-id="element-4054">
            {/* Header */}
            <div className="text-center mb-12" data-id="element-4055">
              <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800 dark:text-[#F8FAF9]" data-id="element-4056">
                Contact Us
              </h1>
              <p className="text-xl text-gray-600 dark:text-[#B0C1BD] max-w-3xl mx-auto" data-id="element-4057">
                Have questions or feedback? We'd love to hear from you. Our team
                is here to help.
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-8 mb-16" data-id="element-4058">
              {/* Contact Form */}
              <div className="lg:w-2/3" data-id="element-4059">
                <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm" data-id="element-4060">
                  <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-[#F8FAF9]" data-id="element-4061">
                    Send Us a Message
                  </h2>
                  {submitted ? <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-lg p-6 text-center" data-id="element-4062">
                      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4" data-id="element-4063">
                        <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" data-id="element-4064" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-[#F8FAF9]" data-id="element-4065">
                        Message Sent!
                      </h3>
                      <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-4066">
                        Thank you for reaching out. We'll get back to you as
                        soon as possible.
                      </p>
                      <button onClick={() => setSubmitted(false)} className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-4067">
                        Send Another Message
                      </button>
                    </div> : <form onSubmit={handleSubmit} data-id="element-4068">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4069">
                        <div data-id="element-4070">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4071">
                            Your Name *
                          </label>
                          <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className={`w-full p-3 border rounded-lg ${errors.name ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-[#1A3330]'} bg-white dark:bg-[#162825] text-gray-800 dark:text-[#F8FAF9]`} placeholder="John Doe" data-id="element-4072" />
                          {errors.name && <p className="mt-1 text-sm text-red-500 dark:text-red-400" data-id="element-4073">
                              {errors.name}
                            </p>}
                        </div>
                        <div data-id="element-4074">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4075">
                            Email Address *
                          </label>
                          <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={`w-full p-3 border rounded-lg ${errors.email ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-[#1A3330]'} bg-white dark:bg-[#162825] text-gray-800 dark:text-[#F8FAF9]`} placeholder="john.doe@example.com" data-id="element-4076" />
                          {errors.email && <p className="mt-1 text-sm text-red-500 dark:text-red-400" data-id="element-4077">
                              {errors.email}
                            </p>}
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-4078">
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4079">
                          Subject *
                        </label>
                        <select id="subject" name="subject" value={formData.subject} onChange={handleInputChange} className={`w-full p-3 border rounded-lg ${errors.subject ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-[#1A3330]'} bg-white dark:bg-[#162825] text-gray-800 dark:text-[#F8FAF9]`} data-id="element-4080">
                          <option value="" data-id="element-4081">Select a subject</option>
                          <option value="General Inquiry" data-id="element-4082">
                            General Inquiry
                          </option>
                          <option value="Product Support" data-id="element-4083">
                            Product Support
                          </option>
                          <option value="Seller Support" data-id="element-4084">Seller Support</option>
                          <option value="Billing Question" data-id="element-4085">
                            Billing Question
                          </option>
                          <option value="Partnership Opportunity" data-id="element-4086">
                            Partnership Opportunity
                          </option>
                          <option value="Other" data-id="element-4087">Other</option>
                        </select>
                        {errors.subject && <p className="mt-1 text-sm text-red-500 dark:text-red-400" data-id="element-4088">
                            {errors.subject}
                          </p>}
                      </div>
                      <div className="mb-6" data-id="element-4089">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4090">
                          Message *
                        </label>
                        <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} rows={5} className={`w-full p-3 border rounded-lg ${errors.message ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-[#1A3330]'} bg-white dark:bg-[#162825] text-gray-800 dark:text-[#F8FAF9]`} placeholder="How can we help you?" data-id="element-4091"></textarea>
                        {errors.message && <p className="mt-1 text-sm text-red-500 dark:text-red-400" data-id="element-4092">
                            {errors.message}
                          </p>}
                      </div>
                      <button type="submit" disabled={loading} className={`w-full py-3 px-4 ${loading ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] hover:shadow-lg'} text-white rounded-lg font-medium transition-all flex items-center justify-center`} data-id="element-4093">
                        {loading ? <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-4094">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4095"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4096"></path>
                            </svg>
                            Sending...
                          </> : <>
                            <MessageSquareIcon className="h-5 w-5 mr-2" data-id="element-4097" />
                            Send Message
                          </>}
                      </button>
                    </form>}
                </div>
              </div>
              {/* Contact Info */}
              <div className="lg:w-1/3" data-id="element-4098">
                <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm" data-id="element-4099">
                  <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-[#F8FAF9]" data-id="element-4100">
                    Contact Information
                  </h2>
                  <div className="flex items-start" data-id="element-4101">
                    <div className="mt-1 bg-[#2DB489]/10 p-2 rounded-full mr-4" data-id="element-4102">
                      <MailIcon className="h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-4103" />
                    </div>
                    <div data-id="element-4104">
                      <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-4105">
                        Email
                      </h3>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4106">
                        support@dgstack.io
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-4107" />
    </div>;
};